/* HR — Invoices & GL (R6 fully wired)
 *
 *  Authorisation: finance / admin only（後端 require_role 限制）。
 *
 *  Endpoints:
 *    Invoices:
 *      GET    /api/invoices?direction=in|out&status=&from_date=&to_date=&page=
 *      POST   /api/invoices                     { invoice_no, direction, ... }
 *      PATCH  /api/invoices/:id
 *      POST   /api/invoices/:id/void
 *      DELETE /api/invoices/:id                 (僅 pending 可刪)
 *
 *    GL Accounts (Chart of Accounts):
 *      GET    /api/gl/accounts?active_only=true
 *      POST   /api/gl/accounts                  { account_code, account_name, account_type, parent_id?, is_active }
 *      PATCH  /api/gl/accounts/:id
 *
 *    GL Entries (Journal):
 *      GET    /api/gl/entries?from_date=&to_date=&is_approved=
 *      POST   /api/gl/entries                   { entry_date, description?, lines: [{account_id, debit/credit}] }
 *      POST   /api/gl/entries/:id/approve       (finance/admin)
 */

const INVOICE_STATUS = {
  pending: { label: '待開立', tone: 'warn' },
  issued:  { label: '已開立', tone: 'accent' },
  voided:  { label: '已作廢', tone: 'down' },
};

/* Render-time function, not a module-level const: HR colours are var(--gx-*) under Expressive
 * and restore() must bring Bento back (SPEC §1.2「內頁換膚」, tests/test_expressive_pages.cjs §7). */
function accountTypeMeta() {
  return {
    asset:     { label: '資產', color: '#2C5282' },
    liability: { label: '負債', color: '#A56A1F' },
    equity:    { label: '權益', color: '#553C7B' },
    revenue:   { label: '收入', color: HR.accent },
    expense:   { label: '費用', color: HR.down },
  };
}

/* ── Expressive (GX) skin helpers — plan-expressive-inner-pages.md §2(d). Render-time only; under
 * Bento they return the ORIGINAL object (uiStyle), so Bento output is unchanged. */
// Uppercase / letter-spaced micro label → GX sentence-case 700 label (onV: AA on every tonal surface).
function _ivGxLabel(bento) {
  return uiStyle(bento, { letterSpacing: 0, textTransform: 'none', fontWeight: 700, color: GxT.color.onV });
}
// Segmented filter button (status / type / approval) → GX M3 filter chip.
function _ivGxFilterChip(bento, selected) {
  return uiStyle(bento, {
    minHeight: 32, padding: '0 12px', borderRadius: GxT.radius.chip, fontWeight: 700, fontFamily: GxT.font,
    border: `1px solid ${selected ? 'transparent' : GxT.color.olv}`,
    background: selected ? GxT.color.sc : GxT.color.card,
    color: selected ? GxT.color.onSc : GxT.color.onV,
  });
}

/* 此頁所有功能對應 capabilities:
 *   invoice.read / invoice.write   → 發票
 *   gl.read / gl.write / gl.approve → 總帳 (accounts + entries + approve)
 * 後端 require_role 對應 finance/admin。 */

/* ─────────────────────────  Invoice form  ─────────────────── */

function InvoiceForm({ initial, defaultDirection, onCancel, onSaved }) {
  const toast = useToast();
  const isEdit = !!initial?.id;
  const [invNo, setInvNo] = React.useState(initial?.invoice_no || '');
  const [date, setDate] = React.useState(initial?.invoice_date || todayStr());
  const [direction, setDirection] = React.useState(initial?.direction || defaultDirection || 'out');
  const [buyerName, setBuyerName] = React.useState(initial?.buyer_name || '');
  const [buyerTaxId, setBuyerTaxId] = React.useState(initial?.buyer_tax_id || '');
  const [sellerName, setSellerName] = React.useState(initial?.seller_name || '');
  const [sellerTaxId, setSellerTaxId] = React.useState(initial?.seller_tax_id || '');
  const [excl, setExcl] = React.useState(initial?.amount_excl_tax ?? 0);
  const [taxRate, setTaxRate] = React.useState(initial?.tax_rate ?? 0.05);
  const [taxAmount, setTaxAmount] = React.useState(initial?.tax_amount ?? '');
  const [status, setStatus] = React.useState(initial?.status || 'pending');
  const [submitting, setSubmitting] = React.useState(false);

  // 自動算 tax_amount 預覽（若使用者沒填）
  const previewTax = React.useMemo(() => {
    if (taxAmount !== '' && taxAmount !== null && taxAmount !== undefined) {
      return parseFloat(taxAmount) || 0;
    }
    return Math.round((parseFloat(excl) || 0) * (parseFloat(taxRate) || 0) * 100) / 100;
  }, [excl, taxRate, taxAmount]);
  const previewTotal = (parseFloat(excl) || 0) + previewTax;

  const submit = async () => {
    if (!invNo.trim()) { toast('發票號碼必填', 'error'); return; }
    setSubmitting(true);
    try {
      const body = {
        invoice_no: invNo.trim(),
        invoice_date: date || null,
        direction,
        buyer_name: buyerName.trim() || null,
        buyer_tax_id: buyerTaxId.trim() || null,
        seller_name: sellerName.trim() || null,
        seller_tax_id: sellerTaxId.trim() || null,
        amount_excl_tax: parseFloat(excl) || 0,
        tax_rate: parseFloat(taxRate) || 0,
        tax_amount: taxAmount === '' ? null : (parseFloat(taxAmount) || 0),
        status,
      };
      if (isEdit) await api('PATCH', `/invoices/${initial.id}`, body);
      else        await api('POST',  '/invoices', body);
      toast(isEdit ? '已更新' : '已新增', 'ok');
      onSaved();
    } catch (e) { toast((isEdit ? '更新' : '新增') + '失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  const isOut = direction === 'out';

  return (
    <Modal
      open onClose={onCancel}
      title={isEdit ? `編輯發票 — ${initial.invoice_no}` : '新增發票'}
      subtitle={isOut ? '銷項（公司開出）' : '進項（公司收到）'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '處理中…' : (isEdit ? '儲存' : '新增')}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <Field label="方向" required>
          <select value={direction} onChange={(e) => setDirection(e.target.value)} style={inputStyle()} disabled={isEdit}>
            <option value="out">銷項 (out)</option>
            <option value="in">進項 (in)</option>
          </select>
        </Field>
        <Field label="發票號碼" required span={2}>
          <TextInput value={invNo} onChange={setInvNo} mono placeholder="e.g. AB-12345678" />
        </Field>
        <Field label="日期" required>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle()} />
        </Field>
        <Field label="狀態" span={2}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle()}>
            <option value="pending">待開立</option>
            <option value="issued">已開立</option>
            <option value="voided">已作廢</option>
          </select>
        </Field>
        <Field label={isOut ? '買方名稱' : '買方名稱（本公司）'} span={2}>
          <TextInput value={buyerName} onChange={setBuyerName} />
        </Field>
        <Field label="買方統編">
          <TextInput value={buyerTaxId} onChange={setBuyerTaxId} mono />
        </Field>
        <Field label={isOut ? '賣方名稱（本公司）' : '賣方名稱'} span={2}>
          <TextInput value={sellerName} onChange={setSellerName} />
        </Field>
        <Field label="賣方統編">
          <TextInput value={sellerTaxId} onChange={setSellerTaxId} mono />
        </Field>
        <Field label="未稅金額" required>
          <input type="number" step="any" value={excl} onChange={(e) => setExcl(e.target.value)} style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} />
        </Field>
        <Field label="稅率" hint="預設 0.05 = 5%">
          <input type="number" step="0.01" min={0} max={1} value={taxRate} onChange={(e) => setTaxRate(e.target.value)} style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} />
        </Field>
        <Field label="稅額" hint="留空 = 自動 (excl × rate)">
          <input type="number" step="any" value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)} style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} placeholder="（選填）" />
        </Field>
      </div>
      <div style={uiStyle({
        marginTop: 18, padding: 14, background: HR.surfaceAlt, borderRadius: 5,
        display: 'grid', gridTemplateColumns: 'auto auto auto', gap: 18,
        fontFamily: HR.mono, fontSize: FontSizes.xs,
      }, { padding: '14px 16px', background: GxT.color.s2, borderRadius: GxT.radius.inner })}>
        <span style={{ color: uiPick(HR.inkFaint, GxT.color.onV) }}>未稅 {fmtMoney(parseFloat(excl) || 0)}</span>
        <span style={{ color: uiPick(HR.inkFaint, GxT.color.onV) }}>+ 稅 {fmtMoney(previewTax)}</span>
        <span style={{ fontWeight: uiPick(600, 800), color: HR.accent, fontSize: FontSizes.base }}>合計 {fmtMoney(previewTotal)}</span>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  Invoices tab  ─────────────────── */

function InvoicesTab({ direction, label, user }) {
  const toast = useToast();
  const canWrite = hasCapability(user, 'invoice.write');
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [editing, setEditing] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [confirmVoid, setConfirmVoid] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('');
  const pageSize = 50;

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set('direction', direction);
    if (statusFilter) qs.set('status', statusFilter);
    qs.set('page', String(page));
    qs.set('page_size', String(pageSize));
    const resp = await api('GET', `/invoices?${qs}`).catch(() => ({ items: [], total: 0 }));
    setInvoices(resp.items || []);
    setTotal(resp.total || 0);
    setLoading(false);
  }, [direction, statusFilter, page]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api('DELETE', `/invoices/${confirmDelete.id}`);
      toast('已刪除', 'ok');
      setConfirmDelete(null);
      refresh();
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
    finally { setBusy(false); }
  };

  const doVoid = async () => {
    if (!confirmVoid) return;
    setBusy(true);
    try {
      await api('POST', `/invoices/${confirmVoid.id}/void`);
      toast('已作廢', 'ok');
      setConfirmVoid(null);
      refresh();
    } catch (e) { toast('作廢失敗：' + (e.detail || ''), 'error'); }
    finally { setBusy(false); }
  };

  const totals = invoices.reduce((acc, inv) => {
    acc.excl += inv.amount_excl_tax || 0;
    acc.tax  += inv.tax_amount     || 0;
    acc.tot  += inv.total_amount   || 0;
    return acc;
  }, { excl: 0, tax: 0, tot: 0 });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <Card className={GxCore.cls(undefined, 'gx-inv-scroll')} padding="0" eyebrow={direction === 'out' ? 'Sales' : 'Purchase'} title={label} italic={`${total} 筆 · 合計 ${fmtMoney(totals.tot)}`} right={
        canWrite && <Button variant="primary" size="md" icon="plus" onClick={() => setEditing({})}>新增發票</Button>
      }>
        <div style={{ padding: '12px 24px', borderBottom: `1px solid ${HR.rule}`, display: 'flex', gap: 10, alignItems: 'center', fontSize: FontSizes.xxs }}>
          <span style={_ivGxLabel({ color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' })}>狀態</span>
          {['', 'pending', 'issued', 'voided'].map(s => (
            <button
              key={s || 'all'}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              aria-pressed={uiPick(undefined, statusFilter === s)}
              style={_ivGxFilterChip({
                padding: '4px 10px', fontSize: FontSizes.xxs, borderRadius: 3, border: `1px solid ${HR.rule}`,
                background: statusFilter === s ? HR.ink : HR.surface,
                color: statusFilter === s ? '#fff' : HR.inkSoft,
                cursor: 'pointer', fontFamily: HR.sans,
              }, statusFilter === s)}
            >{s ? INVOICE_STATUS[s]?.label : '全部'}</button>
          ))}
        </div>
        {loading ? (
          <SkeletonTable rows={6} />
        ) : invoices.length === 0 ? (
          <EmptyState icon="file-text" title="尚無發票" hint="點右上角新增。" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>發票號碼</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>日期</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>對方</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>未稅</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>稅</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>合計</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const isVoided = inv.status === 'voided';
                return (
                  <tr key={inv.id} style={{ borderTop: `1px solid ${HR.ruleSoft}`, opacity: isVoided ? 0.5 : 1 }}>
                    <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, fontWeight: 500 }}>{inv.invoice_no}</td>
                    <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{inv.invoice_date || '—'}</td>
                    <td style={{ padding: '13px 16px' }}>
                      {direction === 'out' ? (inv.buyer_name || '—') : (inv.seller_name || '—')}
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{fmtMoney(inv.amount_excl_tax)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: HR.inkSoft }}>{fmtMoney(inv.tax_amount)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(inv.total_amount)}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <Pill tone={INVOICE_STATUS[inv.status]?.tone || 'neutral'} dot>{INVOICE_STATUS[inv.status]?.label || inv.status}</Pill>
                    </td>
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                      {!isVoided && canWrite && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(inv)}>編輯</Button>
                          {inv.status === 'issued' && (
                            <Button size="sm" variant="ghost" onClick={() => setConfirmVoid(inv)} style={{ color: HR.down }}>作廢</Button>
                          )}
                          {inv.status === 'pending' && (
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(inv)} style={{ color: HR.down }}>刪除</Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div style={{
            padding: '14px 24px', borderTop: `1px solid ${HR.rule}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono,
          }}>
            <span>第 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} / 共 {total} 筆</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>上一頁</Button>
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>下一頁</Button>
            </div>
          </div>
        )}
      </Card>

      {editing !== null && (
        <InvoiceForm
          initial={editing.id ? editing : null}
          defaultDirection={direction}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        title="刪除發票"
        message={confirmDelete ? `確定刪除發票 ${confirmDelete.invoice_no}？此操作無法復原。` : ''}
        danger busy={busy}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
      <ConfirmDialog
        open={!!confirmVoid}
        title="作廢發票"
        message={confirmVoid ? `確定將發票 ${confirmVoid.invoice_no} 標記為「已作廢」？作廢後不可恢復。` : ''}
        danger busy={busy}
        onCancel={() => setConfirmVoid(null)}
        onConfirm={doVoid}
      />
    </>
  );
}

/* ─────────────────────────  GL Account form  ──────────────── */

function AccountForm({ initial, accounts, onCancel, onSaved }) {
  const toast = useToast();
  const isEdit = !!initial?.id;
  const [code, setCode] = React.useState(initial?.account_code || '');
  const [name, setName] = React.useState(initial?.account_name || '');
  const [type, setType] = React.useState(initial?.account_type || 'asset');
  const [parentId, setParentId] = React.useState(initial?.parent_id || '');
  const [isActive, setIsActive] = React.useState(initial?.is_active ?? true);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!code.trim() || !name.trim()) { toast('代號與名稱必填', 'error'); return; }
    setSubmitting(true);
    try {
      const body = {
        account_code: code.trim(),
        account_name: name.trim(),
        account_type: type,
        parent_id: parentId ? parseInt(parentId) : null,
        is_active: isActive,
      };
      if (isEdit) await api('PATCH', `/gl/accounts/${initial.id}`, body);
      else        await api('POST',  '/gl/accounts', body);
      toast(isEdit ? '已更新' : '已新增', 'ok');
      onSaved();
    } catch (e) { toast((isEdit ? '更新' : '新增') + '失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title={isEdit ? `編輯科目 — ${initial.account_name}` : '新增會計科目'}
      subtitle="Chart of Accounts"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '處理中…' : (isEdit ? '儲存' : '新增')}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="代號" required hint="慣例：4 碼數字">
          <TextInput value={code} onChange={setCode} mono />
        </Field>
        <Field label="名稱" required>
          <TextInput value={name} onChange={setName} />
        </Field>
        <Field label="類別" required>
          <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle()}>
            {Object.entries(accountTypeMeta()).map(([k, v]) => (
              <option key={k} value={k}>{v.label} ({k})</option>
            ))}
          </select>
        </Field>
        <Field label="父科目">
          <select value={parentId} onChange={(e) => setParentId(e.target.value)} style={inputStyle()}>
            <option value="">（無）</option>
            {accounts.filter(a => a.id !== initial?.id).map(a => (
              <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>
            ))}
          </select>
        </Field>
        <Field label="啟用" span={2}>
          <select value={isActive ? '1' : '0'} onChange={(e) => setIsActive(e.target.value === '1')} style={inputStyle()}>
            <option value="1">啟用中</option>
            <option value="0">已停用</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  GL Accounts tab  ──────────────── */

function AccountsTab({ user }) {
  const canWrite = hasCapability(user, 'gl.write');
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [typeFilter, setTypeFilter] = React.useState('');

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const list = await api('GET', '/gl/accounts?active_only=false').catch(() => []);
    setAccounts(Array.isArray(list) ? list : []);
    setLoading(false);
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  const filtered = typeFilter ? accounts.filter(a => a.account_type === typeFilter) : accounts;

  return (
    <>
      <Card className={GxCore.cls(undefined, 'gx-inv-scroll')} padding="0" eyebrow="Chart of Accounts" title="會計科目" italic={`${accounts.length} 個科目`} right={
        canWrite && <Button variant="primary" size="md" icon="plus" onClick={() => setEditing({})}>新增科目</Button>
      }>
        <div style={{ padding: '12px 24px', borderBottom: `1px solid ${HR.rule}`, display: 'flex', gap: 10, alignItems: 'center', fontSize: FontSizes.xxs }}>
          <span style={_ivGxLabel({ color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' })}>類別</span>
          {[['', '全部'], ...Object.entries(accountTypeMeta()).map(([k, v]) => [k, v.label])].map(([k, label]) => (
            <button
              key={k || 'all'}
              onClick={() => setTypeFilter(k)}
              aria-pressed={uiPick(undefined, typeFilter === k)}
              style={_ivGxFilterChip({
                padding: '4px 10px', fontSize: FontSizes.xxs, borderRadius: 3, border: `1px solid ${HR.rule}`,
                background: typeFilter === k ? HR.ink : HR.surface,
                color: typeFilter === k ? '#fff' : HR.inkSoft,
                cursor: 'pointer', fontFamily: HR.sans,
              }, typeFilter === k)}
            >{label}</button>
          ))}
        </div>
        {loading ? (
          <SkeletonTable rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon="folder" title="尚未建立科目" hint="點右上角新增。" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>代號</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>名稱</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>類別</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>父科目</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => {
                const tmeta = accountTypeMeta()[a.account_type] || { label: a.account_type || '—', color: HR.inkFaint };
                return (
                  <tr key={a.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                    <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontWeight: 500 }}>{a.account_code}</td>
                    <td style={{ padding: '13px 16px' }}>{a.account_name}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: FontSizes.trading_xl, color: tmeta.color, fontWeight: 500,
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: 1, background: tmeta.color }} />
                        {tmeta.label}
                      </span>
                    </td>
                    <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                      {a.parent_code ? `${a.parent_code} ${a.parent_name}` : '—'}
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      {a.is_active ? <Pill tone="accent" dot>啟用中</Pill> : <Pill tone="neutral">已停用</Pill>}
                    </td>
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                      {canWrite && <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>編輯</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
      {editing !== null && (
        <AccountForm
          initial={editing.id ? editing : null}
          accounts={accounts}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  GL Entry form  ───────────────── */

function makeTmpLine() {
  return { tmpId: Math.random().toString(36).slice(2), account_id: null, debit: 0, credit: 0, description: '' };
}

function EntryForm({ accounts, onCancel, onSaved }) {
  const toast = useToast();
  const [date, setDate] = React.useState(todayStr());
  const [desc, setDesc] = React.useState('');
  const [sourceType, setSourceType] = React.useState('manual');
  const [lines, setLines] = React.useState([makeTmpLine(), makeTmpLine()]);
  const [submitting, setSubmitting] = React.useState(false);

  const addLine = () => setLines(arr => [...arr, makeTmpLine()]);
  const removeLine = (i) => setLines(arr => arr.filter((_, idx) => idx !== i));
  const updLine = (i, k, v) => setLines(arr => arr.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const totalDebit  = lines.reduce((s, l) => s + (parseFloat(l.debit)  || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) <= 0.01 && totalDebit > 0;

  const submit = async () => {
    if (!balanced) { toast('借貸不平衡，無法送出', 'error'); return; }
    if (lines.some(l => !l.account_id)) { toast('每行皆需選擇科目', 'error'); return; }
    setSubmitting(true);
    try {
      await api('POST', '/gl/entries', {
        entry_date: date,
        description: desc.trim() || null,
        source_type: sourceType,
        lines: lines.map((l, idx) => ({
          account_id: parseInt(l.account_id),
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description?.trim() || null,
          sort_order: idx,
        })),
      });
      toast('已建立分錄（待核准）', 'ok');
      onSaved();
    } catch (e) { toast('建立失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="新增傳票分錄"
      subtitle="New Journal Entry"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || !balanced}>{submitting ? '處理中…' : '建立'}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px', gap: 14, marginBottom: 14 }}>
        <Field label="日期" required>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle()} />
        </Field>
        <Field label="摘要">
          <TextInput value={desc} onChange={setDesc} placeholder="e.g. 5月份辦公室租金" />
        </Field>
        <Field label="來源">
          <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} style={inputStyle()}>
            <option value="manual">手動</option>
            <option value="payroll">薪資</option>
            <option value="expense">報帳</option>
          </select>
        </Field>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.base }, { fontWeight: 800 })}>明細（至少 2 行 · 借貸需平衡）</span>
        <Button size="sm" variant="ghost" icon="plus" onClick={addLine} disabled={submitting}>新增行</Button>
      </div>

      <div style={uiStyle({ border: `1px solid ${HR.rule}`, borderRadius: 5, overflow: 'hidden' }, { borderRadius: GxT.radius.inner })}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
          <thead>
            <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
              <th style={{ padding: '10px 12px', textAlign: 'left',  fontWeight: 500, width: 240 }}>科目</th>
              <th style={{ padding: '10px 12px', textAlign: 'left',  fontWeight: 500 }}>摘要</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, width: 130 }}>借方</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, width: 130 }}>貸方</th>
              <th style={{ padding: '10px 8px',  width: 32 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.tmpId} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                <td style={{ padding: '8px 12px' }}>
                  <select value={l.account_id || ''} onChange={(e) => updLine(i, 'account_id', e.target.value)} style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs })}>
                    <option value="">— 選擇科目 —</option>
                    {accounts.filter(a => a.is_active).map(a => (
                      <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <input value={l.description} onChange={(e) => updLine(i, 'description', e.target.value)} style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs })} />
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <input type="number" step="any" value={l.debit} onChange={(e) => { updLine(i, 'debit', e.target.value); if (parseFloat(e.target.value)) updLine(i, 'credit', 0); }} style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs, fontFamily: HR.mono, textAlign: 'right' })} />
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <input type="number" step="any" value={l.credit} onChange={(e) => { updLine(i, 'credit', e.target.value); if (parseFloat(e.target.value)) updLine(i, 'debit', 0); }} style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs, fontFamily: HR.mono, textAlign: 'right' })} />
                </td>
                <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                  {lines.length > 2 && (
                    <button onClick={() => removeLine(i)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: HR.inkFaint, padding: 2, fontSize: FontSizes.md }}>×</button>
                  )}
                </td>
              </tr>
            ))}
            <tr style={{ background: HR.surfaceAlt, borderTop: `2px solid ${HR.rule}` }}>
              <td colSpan={2} style={_ivGxLabel({ padding: '12px 12px', textAlign: 'right', fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint })}>合計</td>
              <td style={{ padding: '12px 12px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(totalDebit)}</td>
              <td style={{ padding: '12px 12px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(totalCredit)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: FontSizes.xxs, fontFamily: HR.mono, color: balanced ? HR.accent : HR.down }}>
        {balanced ? '✓ 借貸平衡' : `✗ 不平衡（差 ${fmtMoney(Math.abs(totalDebit - totalCredit))}）`}
      </div>
    </Modal>
  );
}

/* ─────────────────────────  GL Entries tab  ──────────────── */

function EntriesTab({ user }) {
  const toast = useToast();
  const canWrite = hasCapability(user, 'gl.write');
  const canApprove = hasCapability(user, 'gl.approve');
  const [entries, setEntries] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [approvedFilter, setApprovedFilter] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const pageSize = 50;

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (approvedFilter === 'approved')   qs.set('is_approved', 'true');
    if (approvedFilter === 'unapproved') qs.set('is_approved', 'false');
    qs.set('page', String(page));
    qs.set('page_size', String(pageSize));
    const [resp, accs] = await Promise.all([
      api('GET', `/gl/entries?${qs}`).catch(() => ({ items: [], total: 0 })),
      api('GET', '/gl/accounts?active_only=false').catch(() => []),
    ]);
    setEntries(resp.items || []);
    setTotal(resp.total || 0);
    setAccounts(Array.isArray(accs) ? accs : []);
    setLoading(false);
  }, [approvedFilter, page]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const approve = async (entry) => {
    setBusy(true);
    try {
      await api('POST', `/gl/entries/${entry.id}/approve`, {});
      toast(`已核准 ${entry.entry_no}`, 'ok');
      refresh();
    } catch (e) { toast('核准失敗：' + (e.detail || ''), 'error'); }
    finally { setBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <Card className={GxCore.cls(undefined, 'gx-inv-scroll')} padding="0" eyebrow="Journal" title="傳票分錄" italic={`${total} 筆`} right={
        canWrite && <Button variant="primary" size="md" icon="plus" onClick={() => setShowNew(true)}>新增分錄</Button>
      }>
        <div style={{ padding: '12px 24px', borderBottom: `1px solid ${HR.rule}`, display: 'flex', gap: 10, alignItems: 'center', fontSize: FontSizes.xxs }}>
          <span style={_ivGxLabel({ color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' })}>核准狀態</span>
          {[['', '全部'], ['approved', '已核准'], ['unapproved', '未核准']].map(([k, label]) => (
            <button
              key={k || 'all'}
              onClick={() => { setApprovedFilter(k); setPage(1); }}
              aria-pressed={uiPick(undefined, approvedFilter === k)}
              style={_ivGxFilterChip({
                padding: '4px 10px', fontSize: FontSizes.xxs, borderRadius: 3, border: `1px solid ${HR.rule}`,
                background: approvedFilter === k ? HR.ink : HR.surface,
                color: approvedFilter === k ? '#fff' : HR.inkSoft,
                cursor: 'pointer', fontFamily: HR.sans,
              }, approvedFilter === k)}
            >{label}</button>
          ))}
        </div>
        {loading ? (
          <SkeletonTable rows={6} />
        ) : entries.length === 0 ? (
          <EmptyState icon="file-text" title="尚無分錄" hint="點右上角新增。" />
        ) : (
          <div className={GxCore.cls(undefined, 'gx-inv-entries')}>
            {entries.map(ent => {
              const isOpen = expanded === ent.id;
              return (
                <div key={ent.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <div
                    onClick={() => setExpanded(isOpen ? null : ent.id)}
                    style={{
                      display: 'grid', gridTemplateColumns: '120px 110px 1fr auto auto 16px', gap: 16,
                      padding: '14px 24px', cursor: 'pointer', alignItems: 'center',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xxs, fontWeight: 500 }}>{ent.entry_no}</span>
                    <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{ent.entry_date}</span>
                    <span style={{ fontFamily: HR.serif, fontSize: FontSizes.sm }}>{ent.description || '—'}</span>
                    <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xs, fontWeight: 600 }}>{fmtMoney(ent.total_debit)}</span>
                    {ent.is_approved
                      ? <Pill tone="accent" dot>已核准</Pill>
                      : <Pill tone="warn"   dot>未核准</Pill>}
                    <Icon name="chevron-down" size={12} className={isOpen ? 'rotated' : ''} />
                  </div>
                  {isOpen && (
                    <div style={{ padding: '4px 24px 22px 24px', background: HR.surfaceAlt }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.trading_md }}>
                        <thead>
                          <tr style={{ fontSize: FontSizes.trading_sm, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left',  fontWeight: 500 }}>科目</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left',  fontWeight: 500 }}>摘要</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, width: 120 }}>借方</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, width: 120 }}>貸方</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(ent.lines || []).map(l => (
                            <tr key={l.id} style={{ borderTop: `1px solid ${HR.rule}` }}>
                              <td style={{ padding: '8px 10px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>{l.account_code} {l.account_name}</td>
                              <td style={{ padding: '8px 10px', color: HR.inkSoft }}>{l.description || '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: HR.mono }}>{l.debit > 0 ? fmtMoney(l.debit) : '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: HR.mono }}>{l.credit > 0 ? fmtMoney(l.credit) : '—'}</td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: `2px solid ${HR.rule}`, background: HR.surface }}>
                            <td colSpan={2} style={{ padding: '10px', textAlign: 'right', fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>合計</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(ent.total_debit)}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(ent.total_credit)}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                        <span>建立人：{ent.created_by_name || '—'}</span>
                        {ent.is_approved && (
                          <span>核准人：{ent.approved_by_name || '—'} · 於 {fmtDateTime(ent.approved_at)}</span>
                        )}
                        <span style={{ flex: 1 }} />
                        {!ent.is_approved && canApprove && (
                          <Button size="sm" variant="primary" onClick={() => approve(ent)} disabled={busy}>核准分錄</Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <style>{`.rotated { transform: rotate(180deg); transition: transform 200ms; }`}</style>
          </div>
        )}
        {totalPages > 1 && (
          <div style={{
            padding: '14px 24px', borderTop: `1px solid ${HR.rule}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono,
          }}>
            <span>第 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} / 共 {total} 筆</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>上一頁</Button>
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>下一頁</Button>
            </div>
          </div>
        )}
      </Card>

      {showNew && (
        <EntryForm
          accounts={accounts}
          onCancel={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); refresh(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  Page entry  ─────────────────── */

function InvoicesPage() {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState('sale');

  React.useEffect(() => {
    api('GET', '/auth/me').then(setUser).catch(() => setUser({})).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <>
        <PageHeader eyebrow="財務 · Invoicing & GL" title="發票・總帳" italic="Invoices & General Ledger" />
        <SkeletonTable rows={6} />
      </>
    );
  }

  // Capability gates — 各分頁可獨立開放，避免「只有 GL 但沒發票」這種角色被全擋
  const canInvoice = hasCapability(user, 'invoice.read');
  const canGL = hasCapability(user, 'gl.read');

  if (!canInvoice && !canGL) {
    return (
      <>
        <PageHeader eyebrow="財務 · Invoicing & GL" title="發票・總帳" italic="Invoices & General Ledger" />
        <EmptyState icon="file-text" title="無權檢視" hint="此模組需要 invoice.read 或 gl.read capability。" />
      </>
    );
  }

  const tabs = [
    ...(canInvoice ? [
      { key: 'sale',     label: '銷項發票', icon: 'file-text' },
      { key: 'purchase', label: '進項發票', icon: 'file-text' },
    ] : []),
    ...(canGL ? [
      { key: 'accounts', label: '會計科目', icon: 'folder' },
      { key: 'entries',  label: '傳票分錄', icon: 'layout' },
    ] : []),
  ];

  // 若預設 tab 不在可見清單，自動切到第一個
  const activeTab = tabs.find(t => t.key === tab) ? tab : tabs[0]?.key;

  return (
    <>
      <PageHeader eyebrow="財務 · Invoicing & GL" title="發票・總帳" italic="Invoices & General Ledger" />
      <Tabs tabs={tabs} value={activeTab} onChange={setTab} />
      {activeTab === 'sale'     && <InvoicesTab direction="out" label="銷項發票（公司開出）" user={user} />}
      {activeTab === 'purchase' && <InvoicesTab direction="in"  label="進項發票（公司收到）" user={user} />}
      {activeTab === 'accounts' && <AccountsTab user={user} />}
      {activeTab === 'entries'  && <EntriesTab user={user} />}
    </>
  );
}

// Expressive (GX) page CSS — only inserted under <html data-ui-shell="expressive"> (no-op in Bento;
// router fallback removes every style[id^="gx-css-"]). The three ledger cards carry a GX-only class
// (GxCore.cls) so their body can scroll sideways on phones instead of overflowing (plan §5.3 可用性).
GxCore.injectCss('gx-css-p-invoices', String.raw`
html[data-ui-shell="expressive"] .gx-main .gx-inv-scroll > div:last-child{overflow-x:auto}
html[data-ui-shell="expressive"] .gx-main .gx-inv-scroll > div:last-child > div:first-child{flex-wrap:wrap}
@media (max-width:767px){
html[data-ui-shell="expressive"] .gx-main .gx-inv-scroll > div:last-child > table{min-width:640px}
html[data-ui-shell="expressive"] .gx-main .gx-inv-scroll > div:last-child > div:first-child{padding:12px 16px!important}
html[data-ui-shell="expressive"] .gx-main .gx-inv-entries{min-width:600px}
html[data-ui-shell="expressive"] .gx-main .gx-inv-scroll th,html[data-ui-shell="expressive"] .gx-main .gx-inv-scroll td{white-space:nowrap}
}
`);

window.InvoicesPage = InvoicesPage;
